import {
  Badge,
  Box,
  Button,
  Collapse,
  Flex,
  Icon,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react'
import { faChevronRight, faEdit } from '@fortawesome/pro-duotone-svg-icons'
import { faEyeSlash } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  EmptyTablePlaceholder,
  TableSkeleton,
} from 'BrokerTsLib/src/components'
import CustomTooltip from 'BrokerTsLib/src/components/CustomTooltip'
import { appInfoListContext } from 'BrokerTsLib/src/context'
import type { AppInfoMessage, AppInfoMessagePagedQuery } from 'BrokerTsLib/src/dtos'
import { useGetAllAppInfoMessages } from 'BrokerTsLib/src/hooks/useAppInfoMessage'
import type {
  ColumnDefinition,
  ColumnsDefinition,
} from 'BrokerTsLib/src/interfaces/columnsDefinition'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { createSearchParams, Link } from 'react-router-dom'

import { CustomWaypoint, PageSection } from '@/components'
import { AppInfoDetail } from '@/components/AppInfo/AppInfoDetail'
import { TableSection } from '@/components/Section/TableSection'
import { DashboardHeader } from '@/containers/dashboard/DashboardHeader'
import { Pagination } from '@/containers/Pagination'
import { SidebarProvider, useSidebar } from '@/context/SidebarContext'
import type { IApp } from '@/dtos'
import { useAvailableApps, useAvailableProducts } from '@/hooks/useProducts'
import { Add } from '@/icons'
import { PageWrapper } from '@/layouts/components/PageWrapper'
import type { PageSection as Section } from '@/layouts/components/Sidebar'
import { Sidebar } from '@/layouts/components/Sidebar'
import { formatDate } from '@/utils/formatDate'

export const NewsAdminPage = () => {
  const products = useAvailableProducts()

  return (
    <SidebarProvider>
      <PageWrapper
        header={<DashboardHeader title="Správa aktualit" />}
        sidebar={products.length > 1 ? <Sidebar /> : undefined}
        mainProps={{
          sx: {
            pt: { base: 0, md: 8 },
            gap: 6,
            display: 'grid'
          },
        }}
      >
        <NewsAdmin />
      </PageWrapper>
    </SidebarProvider>
  )
}

const NewsAdmin = () => {
  const apps = useAvailableApps()
  const standaloneApps = apps.filter((x) => x.standalone)
  const { pageSections, setPageSections } = useSidebar()

  useEffect(() => {
    const array: Section[] = []
    let i = 1

    for (const app of standaloneApps) {
      array.push({
        id: app.app,
        sectionTitle: app.name,
        index: i++,
        children: (
          <CustomWaypoint sectionId={app.app}>
            <PageSection id={app.app}>
              <NewsAdminProduct product={app} />
            </PageSection>
          </CustomWaypoint>
        ),
      })
    }

    setPageSections(array)
    return () => setPageSections([])
  }, [apps, setPageSections])

  return pageSections.map((x) => (
    <React.Fragment key={x.id}>{x.children}</React.Fragment>
  ))
}

const defaultPageSize = 10
const NewsAdminProduct = ({ product }: { product: IApp }) => {
  const {
    data,
    mutateAsync: getAllAsync,
    isLoading,
  } = useGetAllAppInfoMessages()
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(defaultPageSize)
  const [filter, setFilter] = useState<AppInfoMessagePagedQuery>({
    pageIndex: currentPage,
    pageSize: defaultPageSize,
    product: product.product,
  })

  const items = data?.data

  const reloadData = useCallback(() => getAllAsync(filter), [filter])

  useEffect(() => {
    reloadData()
  }, [filter])

  useEffect(() => {
    setFilter((val) => ({ ...val, pageSize: pageSize, pageIndex: currentPage }))
  }, [pageSize, currentPage])

  return (
    <TableSection
      sectionTitle={
        <Flex justifyContent={'space-between'}>
          <Text>{product.name}</Text>
          <Button
            colorScheme="primary"
            size="sm"
            variant='link'
            as={Link}
            to={`/news/admin/create?${createSearchParams({
              product: product.product.toString(),
            }).toString()}`}
          >
            <Add />
            Vytvořit novou aktualitu
          </Button>
        </Flex>
      }
      wide
    >
      <appInfoListContext.Provider
        value={{
          loading: isLoading,
          totalCount: items?.totalCount ?? 0,
          currentPage: currentPage,
          pageSize: pageSize,
          filter: filter,
          setCurrentPage: setCurrentPage,
          setPageSize: setPageSize,
          updateFilter: setFilter,
          reload: reloadData,
        }}
      >
        <AppInfoList
          items={items?.items}
          columnsDefinition={[
            ...DefaultAppInfoColumns,
            {
              key: 'edit',
              name: '',
              cellExpr: (item) => (
                <Button
                  px={2}
                  variant="ghost"
                  as={Link}
                  to={`${item.id}/edit`}
                  title='Upravit'
                >
                  <Icon as={FontAwesomeIcon} icon={faEdit} />
                </Button>
              ),
              cellProps: { px: 1 },
              headerCellProps: { w: 0 },
            },
          ]}
        />
        <Pagination context={appInfoListContext} />
      </appInfoListContext.Provider>
    </TableSection>
  )
}

const AppInfoListImportanceColumn: ColumnDefinition<AppInfoMessage> = {
  key: 'importance',
  name: '',
  cellExpr: (item) => {
    const dotSize = '8px'
    const color =
      item.messageImportance === 3
        ? 'red.500'
        : item.messageImportance === 2
          ? 'yellow.500'
          : 'blue.500'
    return (
      <Box
        sx={{
          display: 'inline-block',
          borderRadius: dotSize,
          width: dotSize,
          height: dotSize,
          backgroundColor: color,
          m: 2,
          ml: 4,
          verticalAlign: 'middle',
          _groupHover: { transform: 'scale(1.25)' },
        }}
      />
    )
  },
  cellLabel: (item) =>
    item.messageImportance === 3
      ? 'Nedostupnost'
      : item.messageImportance === 2
        ? 'Vysoká'
        : 'Standardní',
  cellProps: { p: 0 },
  headerCellProps: { w: 0 },
}

const AppInfoListDateColumn: ColumnDefinition<AppInfoMessage> = {
  key: 'messageDate',
  name: 'Datum',
  cellExpr: (item) => (
    <Text fontWeight="light">{formatDate(item.messageDate!)}</Text>
  ),
  cellProps: { textAlign: 'right', px: 3 },
  headerCellProps: { fontWeight: 'bold', textAlign: 'left', w: 0 },
}

const AppInfoListHeaderColumn: ColumnDefinition<AppInfoMessage> = {
  key: 'messageHeader',
  name: 'Nadpis',
  cellExpr: (item) => <Text fontWeight="medium">{item.messageHeader}</Text>,
  headerCellProps: { fontWeight: 'bold' },
}

const AppInfoListTypeColumn: ColumnDefinition<AppInfoMessage> = {
  key: 'show',
  name: '',
  cellExpr: (item) =>
    item.type && <Badge colorScheme="blue">{item.type}</Badge>,
  cellProps: { px: 1 },
  headerCellProps: { w: 0 },
}

const AppInfoListVisibilityColumn: ColumnDefinition<AppInfoMessage> = {
  key: 'visibility',
  name: '',
  cellExpr: (item) =>
    !item.messageShow && (
      <CustomTooltip label='Skrytá' hasArrow>
        <Icon as={FontAwesomeIcon} icon={faEyeSlash} />
      </CustomTooltip>
    ),
  cellProps: { px: 3 },
  headerCellProps: { w: 0 },
}

const DefaultAppInfoColumns: ColumnsDefinition<AppInfoMessage> = [
  AppInfoListImportanceColumn,
  AppInfoListDateColumn,
  AppInfoListHeaderColumn,
  AppInfoListTypeColumn,
  AppInfoListVisibilityColumn,
]

const AppInfoList = ({
  items,
  columnsDefinition = DefaultAppInfoColumns,
}: {
  items?: AppInfoMessage[]
  columnsDefinition?: ColumnsDefinition<AppInfoMessage>
}) => {
  const { pageSize, loading } = useContext(appInfoListContext)
  const [openId, setOpenId] = useState<number | null>(null)

  return (
    <Table>
      <Thead>
        <Tr>
          {columnsDefinition &&
            columnsDefinition.map((column) => (
              <Th
                key={column.key}
                _empty={{ w: 0, p: 0 }}
                {...column.headerCellProps}
              >
                {column.name}
              </Th>
            ))}
          <Th w={0} />
        </Tr>
      </Thead>
      <Tbody>
        <TableSkeleton
          rowsCount={pageSize}
          colSpan={columnsDefinition.length + 1}
          isLoaded={!loading}
        >
          <EmptyTablePlaceholder
            text="Žádné aktuality"
            colSpan={columnsDefinition.length + 1}
          >
            {items?.map((item) => (
              <AppInfoListItem
                key={item.id}
                item={item}
                isOpen={item.id === openId}
                onClick={() => setOpenId(openId === item.id ? null : item.id)}
                columnsDefinition={columnsDefinition}
              />
            ))}
          </EmptyTablePlaceholder>
        </TableSkeleton>
      </Tbody>
    </Table>
  )
}

const AppInfoListItem = ({
  item,
  columnsDefinition,
  isOpen,
  onClick,
}: {
  item: AppInfoMessage
  columnsDefinition: ColumnsDefinition<AppInfoMessage>
  isOpen: boolean
  onClick: () => void
}) => {
  return (
    <>
      <Tr
        border="none"
        key={item.id}
        role="group"
        onClick={onClick}
        cursor="pointer"
      >
        {columnsDefinition &&
          columnsDefinition.map((column) =>
            column.cellLabel ? (
              <Td key={column.key} _empty={{ p: 0 }} {...column.cellProps}>
                <CustomTooltip label={column.cellLabel(item)}>
                  {column.cellExpr(item)}
                </CustomTooltip>
              </Td>
            ) : (
              <Td key={column.key} _empty={{ p: 0 }} {...column.cellProps}>
                {column.cellExpr(item)}
              </Td>
            ),
          )}
        <Td pl={1} pr={2} textAlign="center">
          <Button
            px={2}
            variant="ghost">
            <Icon as={FontAwesomeIcon} icon={faChevronRight}
              transform={isOpen ? 'rotate(90deg)' : 'none'} />
          </Button>
        </Td>
      </Tr>
      <Tr>
        <Td
          colSpan={2}
          p={0}
          borderBottom={isOpen ? '1px' : 'none'}
          borderBottomColor="gray.100"
        ></Td>
        <Td
          colSpan={columnsDefinition.length + 1}
          p={0}
          borderBottom={isOpen ? '1px' : 'none'}
          borderBottomColor="gray.100"
        >
          <Collapse in={isOpen}>
            <AppInfoDetail p={4} appInfo={item}></AppInfoDetail>
          </Collapse>
        </Td>
      </Tr>
    </>
  )
}
